/**
 * The cartela picker — replaces the old "Join this round?" confirmation.
 *
 * Tapping a number buys it immediately; there is no separate confirm step.
 * The entry fee was already shown on the button that opened this screen (§
 * `roundfoot`'s "Join · ETB10"), so asking again here would just be the same
 * question twice. A tap here *is* the confirmation.
 *
 * Every number is either taken (someone else already holds it — grey), mine
 * (green — a player may hold several at once, up to the round's
 * `maxCardsPerUser`), or free (tappable). Taken state comes from the server
 * (`useRound`'s `takenCartelas`, seeded by a fetch and kept live over the
 * socket) — this component never guesses.
 */

import { memo } from 'react';

/**
 * The picker grid's size when a round doesn't say otherwise — must match the
 * `COALESCE($11, 200)` default in `createRound`
 * (`packages/api/src/domain/game/rounds.ts`). A round's actual size travels
 * over the wire as `round.cartelaPoolSize` (frozen at creation, migration
 * 024) and is what callers should pass as `poolSize`; this is only the
 * fallback for a caller that doesn't have a round yet.
 */
export const MAX_CARTELA_NUMBER = 200;

interface PickerCellProps {
  n: number;
  isMine: boolean;
  isTaken: boolean;
  isPending: boolean;
  disabled: boolean;
  onPick(n: number): void;
}

/**
 * Its own memoized component, not just a mapped `<button>` — at Arif
 * Bingo's 1200-slot grid, a naive map re-diffs every cell on each socket
 * `takenCartelas` update; this keeps that diff to just the cell(s) that
 * actually changed.
 */
const PickerCell = memo(function PickerCell({
  n,
  isMine,
  isTaken,
  isPending,
  disabled,
  onPick,
}: PickerCellProps) {
  return (
    <button
      type="button"
      role="gridcell"
      className={[
        'picker__cell',
        isMine ? 'picker__cell--mine' : '',
        isTaken ? 'picker__cell--taken' : '',
        isPending ? 'picker__cell--pending' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled}
      aria-label={isMine ? `${n}, your cartela` : isTaken ? `${n}, taken` : `${n}`}
      onClick={() => onPick(n)}
    >
      {n}
    </button>
  );
});

interface CartelaPickerProps {
  /** The picker grid's size — pass `round.cartelaPoolSize`, not the {@link MAX_CARTELA_NUMBER} fallback. */
  poolSize?: number;
  takenCartelas: ReadonlySet<number>;
  /** Numbers this player already holds in this round — shown green. */
  myCartelaNumbers: ReadonlySet<number>;
  /** The number currently mid-purchase, disabled and visually pending. */
  pendingNumber: number | null;
  onPick(n: number): void;
}

export const CartelaPicker = memo(function CartelaPicker({
  poolSize = MAX_CARTELA_NUMBER,
  takenCartelas,
  myCartelaNumbers,
  pendingNumber,
  onPick,
}: CartelaPickerProps) {
  return (
    <div className="picker" role="grid" aria-label="Choose a cartela">
      {Array.from({ length: poolSize }, (_, i) => i + 1).map((n) => (
        <PickerCell
          key={n}
          n={n}
          isMine={myCartelaNumbers.has(n)}
          isTaken={!myCartelaNumbers.has(n) && takenCartelas.has(n)}
          isPending={n === pendingNumber}
          disabled={myCartelaNumbers.has(n) || takenCartelas.has(n) || pendingNumber !== null}
          onPick={onPick}
        />
      ))}
    </div>
  );
});
